import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, ShieldCheck } from "lucide-react";
import api from "../../lib/Urls";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const editableFields = [
  ["username", "Username", "text"],
  ["email", "Email", "email"],
  ["phone", "Phone", "tel"],
  ["whatsapp", "WhatsApp", "tel"],
  ["country", "Country", "text"],
  ["city", "City", "text"],
  ["location", "Location", "text"],
];

const AdminUserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadUser = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get(`/api/admin/users/${userId}`);
      setUser(res.data);
      setForm({
        username: res.data?.username || "",
        email: res.data?.email || "",
        phone: res.data?.phone ?? "",
        whatsapp: res.data?.whatsapp ?? "",
        country: res.data?.country || "",
        city: res.data?.city || "",
        location: res.data?.location || "",
      });
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/", { replace: true });
        return;
      }
      setError(err.response?.data?.message || "Unable to load this user.");
    } finally {
      setLoading(false);
    }
  }, [navigate, userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setMessage("");
    setError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");

    try {
      const res = await api.put(`/api/admin/users/${userId}`, form);
      setUser(res.data);
      setForm({
        username: res.data?.username || "",
        email: res.data?.email || "",
        phone: res.data?.phone ?? "",
        whatsapp: res.data?.whatsapp ?? "",
        country: res.data?.country || "",
        city: res.data?.city || "",
        location: res.data?.location || "",
      });
      setMessage("User details updated.");
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/", { replace: true });
        return;
      }
      setError(err.response?.data?.message || err.response?.data?.error || "Unable to update user.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="container mx-auto flex-1 px-4 py-8">
        <div className="mx-auto max-w-2xl">
          <div className="mb-6 flex items-center gap-3">
            <Link
              to="/admin/users"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card hover:bg-muted"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                <h1 className="font-heading text-2xl font-bold">User Details</h1>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Sensitive authentication fields and role are deliberately not editable here.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
              Loading user...
            </div>
          ) : error && !user ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {error}
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-5 rounded-xl border border-border bg-card p-5 sm:p-6">
              <div className="flex items-center gap-3 border-b border-border pb-5">
                <img
                  src={
                    user?.profileImageUrl ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || "User")}&background=random&color=fff&size=96`
                  }
                  alt=""
                  className="h-14 w-14 rounded-full border border-border object-cover"
                />
                <div className="min-w-0">
                  <p className="truncate font-semibold">{user?.username}</p>
                  <p className="truncate text-xs text-muted-foreground">User ID: {user?.userId}</p>
                  <div className="mt-1 flex gap-2 text-[11px]">
                    <span className="rounded-full bg-secondary px-2 py-0.5">Role: {user?.role || "user"}</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                      {user?.signInFlag || "offline"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {editableFields.map(([name, label, type]) => (
                  <label key={name} className={name === "email" || name === "location" ? "sm:col-span-2" : ""}>
                    <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</span>
                    <input
                      type={type}
                      name={name}
                      value={form[name] ?? ""}
                      onChange={handleChange}
                      required={name === "username" || name === "email"}
                      className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>
                ))}
              </div>

              {message && (
                <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
                  {message}
                </div>
              )}
              {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminUserDetails;
